import React, { useState } from 'react';
import { VehiculeConfig, PersonnelConfig } from '../../../../../../shared/types/config';

function syncGradePriority(form: Omit<VehiculeConfig, 'id'>, idGradesPriorite: number[]): Omit<VehiculeConfig, 'id'> {
  return { ...form, id_grades_priorite: idGradesPriorite, id_grades_armement: idGradesPriorite };
}

interface VehiculeGradePriorityProps {
  vehiculeForm: Omit<VehiculeConfig, 'id'>;
  setVehiculeForm: React.Dispatch<React.SetStateAction<Omit<VehiculeConfig, 'id'>>>;
  personnelConfigs: PersonnelConfig[];
}

const VehiculeGradePriority: React.FC<VehiculeGradePriorityProps> = ({
  vehiculeForm,
  setVehiculeForm,
  personnelConfigs,
}) => {
  const [searchGradesPrio, setSearchGradesPrio] = useState('');
  const [gradeToAppend, setGradeToAppend] = useState<number | ''>('');

  const gradesForService = personnelConfigs.filter(
    (p) => p.id_service === vehiculeForm.id_service && p.id != null && vehiculeForm.id_service > 0
  );
  const idGradesPrio = vehiculeForm.id_grades_priorite ?? vehiculeForm.id_grades_armement ?? [];

  const moveGrade = (index: number, dir: -1 | 1) => {
    const next = [...idGradesPrio];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    const t = next[index]; next[index] = next[j]; next[j] = t;
    setVehiculeForm(syncGradePriority(vehiculeForm, next));
  };

  const removeGradeAt = (index: number) => {
    setVehiculeForm(syncGradePriority(vehiculeForm, idGradesPrio.filter((_, i) => i !== index)));
  };

  const appendGrade = (personnelId: number) => {
    if (!personnelId || idGradesPrio.includes(personnelId)) return;
    setVehiculeForm(syncGradePriority(vehiculeForm, [...idGradesPrio, personnelId]));
    setGradeToAppend('');
  };

  return (
    <div className="col-span-2">
      <label className="block text-xs font-medium text-cockpit-text-2 mb-1.5">
        Priorité des grades sur l'engin
        {idGradesPrio.length > 0 && (
          <span className="ml-2 text-xs text-[#c7d2fe]">({idGradesPrio.length} en ordre)</span>
        )}
      </label>
      <p className="text-xs text-cockpit-muted mb-2">
        Ordre d'affichage / hiérarchie des postes (chef d'agrès, conducteur, etc.). Ne remplace pas les compétences d'armement.
      </p>
      {!vehiculeForm.id_service ? (
        <p className="text-cockpit-text-2 text-sm">Choisissez un service pour lister les grades.</p>
      ) : gradesForService.length === 0 ? (
        <p className="text-cockpit-text-2 text-sm">Aucun grade pour ce service (onglet Personnels).</p>
      ) : (
        <>
          <div className="rounded-lg border border-[#223046] bg-[#0b1220]/80 p-3 mb-3 space-y-2 min-h-[3rem]">
            {idGradesPrio.length === 0 ? (
              <p className="text-cockpit-muted text-sm">Aucun ordre défini (optionnel).</p>
            ) : (
              idGradesPrio.map((pid, index) => {
                const label = personnelConfigs.find((p) => p.id === pid)?.grade ?? `ID ${pid}`;
                return (
                  <div
                    key={`${pid}-${index}`}
                    className="flex items-center gap-2 flex-wrap rounded border border-[#1a2537]/60 bg-[#0b1220]/60 px-2 py-1.5"
                  >
                    <span className="text-xs text-cockpit-muted w-6 tabular-nums">{index + 1}.</span>
                    <span className="text-sm text-cockpit-text flex-1 min-w-[8rem]">{label}</span>
                    <button
                      type="button"
                      title="Monter"
                      disabled={index === 0}
                      onClick={() => moveGrade(index, -1)}
                      className="rounded border border-cockpit-line/60 bg-cockpit-bg-2/30 px-2 py-0.5 text-xs text-cockpit-text hover:bg-cockpit-bg-2/45 disabled:opacity-40 transition-colors"
                    >↑</button>
                    <button
                      type="button"
                      title="Descendre"
                      disabled={index === idGradesPrio.length - 1}
                      onClick={() => moveGrade(index, 1)}
                      className="rounded border border-cockpit-line/60 bg-cockpit-bg-2/30 px-2 py-0.5 text-xs text-cockpit-text hover:bg-cockpit-bg-2/45 disabled:opacity-40 transition-colors"
                    >↓</button>
                    <button
                      type="button"
                      title="Retirer"
                      onClick={() => removeGradeAt(index)}
                      className="rounded border border-red-500/35 bg-red-500/10 px-2 py-0.5 text-xs text-red-300 hover:bg-red-500/20 transition-colors"
                    >×</button>
                  </div>
                );
              })
            )}
          </div>
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[12rem]">
              <label className="block text-xs text-cockpit-text-2 mb-1">Ajouter un grade à la fin</label>
              <select
                value={gradeToAppend === '' ? '' : String(gradeToAppend)}
                onChange={(e) => { const v = e.target.value ? parseInt(e.target.value, 10) : ''; setGradeToAppend(v === '' ? '' : v); }}
                className="w-full rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text focus:outline-none focus:border-[#6366f1]/60"
              >
                <option value="">— Choisir —</option>
                {gradesForService
                  .filter((p) => p.id != null && !idGradesPrio.includes(p.id))
                  .filter((p) => searchGradesPrio ? p.grade.toLowerCase().includes(searchGradesPrio.toLowerCase()) : true)
                  .map((p) => (
                    <option key={p.id} value={p.id!}>{p.grade}</option>
                  ))}
              </select>
            </div>
            <button
              type="button"
              disabled={gradeToAppend === ''}
              onClick={() => typeof gradeToAppend === 'number' && appendGrade(gradeToAppend)}
              className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-3 py-2 text-sm font-medium text-[#c7d2fe] hover:bg-[#6366f1]/30 disabled:opacity-40 transition-colors"
            >
              Ajouter
            </button>
          </div>
          {gradesForService.length > 6 && (
            <input
              type="text"
              placeholder="Filtrer la liste des grades à ajouter..."
              value={searchGradesPrio}
              onChange={(e) => setSearchGradesPrio(e.target.value)}
              className="mt-2 w-full rounded border border-[#223046] bg-[#0b1220] px-3 py-1.5 text-xs text-cockpit-text focus:outline-none"
            />
          )}
        </>
      )}
    </div>
  );
};

export default VehiculeGradePriority;
